"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserDirectory = void 0;
const uuid_1 = require("uuid");
const mappers_1 = require("../mappers");
const models_1 = require("../models");
const roleAccess_1 = require("../roleAccess/roleAccess");
const socket_1 = require("../socket");
const Observable_1 = require("../socket/Observable");
const utils_1 = require("../utils");
const filterArray_1 = require("../utils/filterArray");
const DomainEvents_1 = require("../utils/DomainEvents");
class UserDirectory {
    constructor(organizationUser, httpAdapter) {
        this.userDictionary = new Map();
        this.removeListeners = [];
        this.syncUserDirectoryListeners = [];
        this.userDirectoryListeners = [];
        this.workloadRequestsInFlight = new Map();
        this.getUserInfo = (id) => {
            var _a;
            const user = (_a = this.userDictionary) === null || _a === void 0 ? void 0 : _a.get(id);
            if (!user) {
                console.error(`Userinfo not found for this user and message ==> UserId: ${id}`);
                return {
                    id,
                    name: "Unknown User",
                    firstName: "Unknown",
                    lastName: "User",
                    title: "",
                    image: "",
                    status: models_1.USER_STATUS.ACTIVE,
                    type: models_1.USER_TYPE.USER,
                    organizationId: this.organization.organizationId,
                    about: "",
                    groupType: models_1.GROUP_TYPE.PRIVATE,
                    chatType: models_1.CHAT_TYPE.PRIVATE,
                };
            }
            return user;
        };
        this.organizationUser = organizationUser;
        this.organization = organizationUser.getOrganization();
        this.httpAdapter = httpAdapter;
        this.currentUserProfile = this.organization.userProfile;
    }
    init() {
        if (this.removeListeners && this.removeListeners.length > 0) {
            throw new Error("Panic: Listeners should have been destoryed during unmount!!!");
        }
        this.removeListeners.push(this.organizationUser
            .getNetworkManager()
            .registerSocketAuthSuccessListener((lastHeartbeatReceivedAt, abortContext) => __awaiter(this, void 0, void 0, function* () {
            yield this.syncUserDirectory(abortContext);
        })));
        this.removeListeners.push(this.updateUserListener(() => {
            this.notifyChange();
        }));
    }
    destroy() {
        this.removeAllListeners();
    }
    removeAllListeners() {
        this.removeListeners.forEach(removeListener => removeListener());
        this.removeListeners = [];
    }
    fetchUserDirectory(organizationId, source, abortContext) {
        return __awaiter(this, void 0, void 0, function* () {
            const userDirectoryResult = yield this.httpAdapter
                .getUserDirectoryAdapter()
                .getUserDirectory(organizationId, source, abortContext);
            if (!userDirectoryResult.isAborted) {
                const userDirectoryData = (0, mappers_1.userDirectoryMapper)(userDirectoryResult.data);
                userDirectoryData.forEach(userInfo => {
                    this.setUserIfNewer(userInfo);
                });
                return (0, utils_1.CompletedResult)(userDirectoryData);
            }
            return utils_1.AbortedResult;
        });
    }
    fetchAppDirectory(source, abortContext) {
        return __awaiter(this, void 0, void 0, function* () {
            const appDirectoryResult = yield this.httpAdapter
                .getUserDirectoryAdapter()
                .getAppDirectory(source, abortContext);
            if (!appDirectoryResult.isAborted) {
                const appDirectoryData = (0, mappers_1.appDirectoryMapper)(appDirectoryResult.data, this.organizationUser.getOrganization().organizationId);
                appDirectoryData.forEach(userInfo => {
                    this.setUserIfNewer(userInfo);
                });
            }
        });
    }
    syncUserDirectory(abortContext) {
        return __awaiter(this, void 0, void 0, function* () {
            const { userDirectory: userDirectoryResult, } = yield this.fetchDirectory(this.organizationUser.getOrganization().organizationId, utils_1.HHNConstants.HEADER_SOURCE_SYNC, abortContext);
            if (!userDirectoryResult.isAborted) {
                this.syncUserDirectoryListeners.forEach(l => l.cb(this.getUserDirectory()));
            }
        });
    }
    registerSyncUserDirectoryListener(cb) {
        const id = (0, uuid_1.v4)();
        this.syncUserDirectoryListeners.push({ id, cb });
        return () => {
            this.syncUserDirectoryListeners = this.syncUserDirectoryListeners.filter(listner => listner.id !== id);
        };
    }
    getUserDirectory(filters) {
        // The directory API returns self too; filter it out here so list
        // consumers (pickers, directories, mention/forward-to/add-member) keep
        // excluding the current user. Reactive self-updates still flow because
        // the dictionary entry exists for getUserInfo reads.
        const selfId = this.organization.userProfile.id;
        const userDirectoryData = [...this.userDictionary.values()].filter(u => u.id !== selfId);
        if (filters) {
            return (0, filterArray_1.filterArray)(userDirectoryData, filters);
        }
        return userDirectoryData;
    }
    getUserDirectoryWithoutApps(filters) {
        const userDirectoryData = this.getUserDirectory(filters);
        return userDirectoryData.filter(userInfo => userInfo.type !== models_1.USER_TYPE.APP);
    }
    sortOnFirstAndLast(userInfoA, userInfoB) {
        const a = userInfoA.name.toLowerCase(), b = userInfoB.name.toLowerCase();
        if (a > b)
            return 1;
        else if (a < b)
            return -1;
        return 0;
    }
    getActiveUserDirectory(filters) {
        const filter = Object.assign({ status: (status) => status === models_1.USER_STATUS.ACTIVE }, filters);
        const canShowFullUserDirectory = this.organizationUser.hasAccess(roleAccess_1.FEATURES.SHOW_FULL_USER_DIRECTORY);
        if (canShowFullUserDirectory) {
            return this.getUserDirectoryWithoutApps(filter).sort(this.sortOnFirstAndLast);
        }
        else {
            const filteredUserDirectory = Object.assign({ status: (status) => status === models_1.USER_STATUS.ACTIVE }, {
                type: (type) => type !== models_1.USER_TYPE.PATIENT && type !== models_1.USER_TYPE.PHYSICIAN,
            });
            return this.getUserDirectoryWithoutApps(filteredUserDirectory).sort(this.sortOnFirstAndLast);
        }
    }
    //TODO change the below function's name
    getFilteredActiveUserDirectory(filters) {
        const canAddExternalUsers = this.organizationUser.hasAccess(roleAccess_1.FEATURES.ADD_EXTERNAL_USER_IN_PATIENT_GROUP);
        const filterInternalUsers = (type) => type === models_1.USER_TYPE.USER ||
            type === models_1.USER_TYPE.STAFF ||
            type === models_1.USER_TYPE.ADMIN;
        const allFilters = canAddExternalUsers
            ? filters
            : Object.assign(Object.assign({}, filters), { type: filterInternalUsers });
        return this.getActiveUserDirectory(allFilters);
    }
    filterQuery(filterData, userInfo) {
        let result = false;
        if (filterData.userType.length > 0 && userInfo.type) {
            result = filterData.userType.includes(userInfo.type);
        }
        if (filterData.userRole.length > 0 && userInfo.department) {
            result = filterData.userRole.includes(userInfo.department.shortHand);
        }
        if (filterData.userType.length > 0 && filterData.userRole.length > 0) {
            result = !!(userInfo.type &&
                filterData.userType.includes(userInfo.type) &&
                userInfo.department &&
                filterData.userRole.includes(userInfo.department.shortHand));
        }
        return result;
    }
    filterUserDirectory(filterData) {
        const data = this.getActiveUserDirectory();
        return data && data.length > 0 && filterData
            ? data.filter((userInfo) => this.filterQuery(filterData, userInfo))
            : data;
    }
    searchByUserName(text, directory) {
        return (0, utils_1.searchUsersByName)(text, directory);
    }
    fetchUserWorkloadDetails(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            const inFlightRequest = this.workloadRequestsInFlight.get(userId);
            if (inFlightRequest) {
                return inFlightRequest;
            }
            const request = this.httpAdapter
                .getUserDirectoryAdapter()
                .getUserWorkload(userId)
                .then(response => {
                const userInfo = this.getUserInfo(userId);
                return (0, mappers_1.workloadCountMapper)(response, userInfo);
            })
                .catch((e) => {
                throw new utils_1.HhnException(e);
            })
                .finally(() => {
                this.workloadRequestsInFlight.delete(userId);
            });
            this.workloadRequestsInFlight.set(userId, request);
            return request;
        });
    }
    updateUserListener(cb) {
        return Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(socket_1.DomainEvents.USER_UPDATE, this.organization.organizationId), (userInfoResponse) => this.updateUser(cb, userInfoResponse));
    }
    updateUser(cb, userInfoResponse) {
        const mappedUserInfo = (0, mappers_1.userInfoMapper)(userInfoResponse);
        if (mappedUserInfo.id && this.setUserIfNewer(mappedUserInfo)) {
            cb();
        }
    }
    registerChangeListener(cb) {
        const id = (0, uuid_1.v4)();
        this.userDirectoryListeners.push({ id, cb });
        return () => {
            this.userDirectoryListeners = this.userDirectoryListeners.filter(listener => listener.id !== id);
        };
    }
    notifyChange() {
        this.userDirectoryListeners.forEach(l => l.cb(this));
    }
    setUserIfNewer(user) {
        const existing = this.userDictionary.get(user.id);
        if ((existing === null || existing === void 0 ? void 0 : existing.updatedAt) &&
            user.updatedAt &&
            existing.updatedAt > user.updatedAt) {
            return false;
        }
        this.userDictionary.set(user.id, user);
        return true;
    }
    addUser(user) {
        if (!this.userDictionary.has(user.id)) {
            this.userDictionary.set(user.id, user);
            this.notifyChange();
        }
    }
    fetchDirectory(organizationId, source, abortContext) {
        return __awaiter(this, void 0, void 0, function* () {
            const [userDirectory] = yield Promise.all([
                this.fetchUserDirectory(organizationId, source, abortContext),
                this.fetchAppDirectory(source, abortContext),
            ]);
            return { userDirectory };
        });
    }
}
exports.UserDirectory = UserDirectory;
